import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react'
import { Badge } from '@/components/ui/badge'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Eye, EyeOff, RefreshCw, ClipboardPaste,
  CheckCircle2, XCircle, Timer, Gauge, ShieldAlert, Clock3, Layers,
  Crosshair, SkipForward, ListChecks,
} from 'lucide-react'
import { screenshotUrl, startVnc, stopVnc } from '../api'
import type { CampaignStats } from '../api'

const TOUCH_CURSOR_CSS = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABcAAAAXCAQAAABKIxwrAAAA1UlEQVR4XpWUMaqEMBRFY6+FhXvQ3i7tVMK4g0y8DHZxdmUxa7NwCecj/CKEAXmc9iRcXu6LI6eio8czE5jx9HRUuZHLDRMqYaL5pQ8EdGiP2zKudRrXbdnjIURgKPUHQt9Xm9wnp03fF0I8cn1AZ3y+MzHj+T4jYvjXaQiokIsDiEBz6RXTFaNQCq5ITFSODh0qM5e06RCic/Roj+5zxx4RvcOjbbnXtwXhHTMa13t9XBGzI6A63et1QgTj7cbsxskY5258VVNnTI009d2+TfZdNf4EfwHXo4ZtrZHBAAAAAElFTkSuQmCC") 11 11, none`

const CAPTCHA_LABELS: Record<string, string> = {
  'CAPTCHA: Обнаружена CheckBox': 'CheckBox',
  'CAPTCHA: Капча усложнена, SmartCaptcha IMG': 'SmartCaptcha IMG',
  'CAPTCHA: Капча усложнена, TextCaptcha': 'TextCaptcha',
  'CAPTCHA: Капча усложнена, SliderCaptcha': 'SliderCaptcha',
  'CAPTCHA: Эскалация → SliderCaptcha': 'SliderCaptcha',
  'CAPTCHA: Эскалация → SmartCaptcha IMG': 'SmartCaptcha IMG',
  'CAPTCHA: Эскалация → TextCaptcha': 'TextCaptcha',
  'Обнаружили SmartCaptcha IMG': 'SmartCaptcha IMG',
  'Обнаружили CheckBox': 'CheckBox',
  'Обнаружили TXT': 'TextCaptcha',
  'Обнаружили SliderCaptcha': 'SliderCaptcha',
  'Яндекс Капча': 'SmartCaptcha',
}

function pluralRu(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n)
  const mod10 = abs % 10
  const mod100 = abs % 100
  if (mod100 >= 11 && mod100 <= 19) return `${n} ${many}`
  if (mod10 === 1) return `${n} ${one}`
  if (mod10 >= 2 && mod10 <= 4) return `${n} ${few}`
  return `${n} ${many}`
}

function formatDurationRu(totalSec: number): string {
  if (!totalSec || totalSec < 60) return '< 1 минуты'
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const parts: string[] = []
  if (h > 0) parts.push(pluralRu(h, 'час', 'часа', 'часов'))
  if (m > 0) parts.push(pluralRu(m, 'минута', 'минуты', 'минут'))
  return parts.join(' ')
}

function formatElapsed(sec: number): string {
  if (!sec) return '—'
  return formatDurationRu(sec)
}

/** Короткая длительность: до минуты — секунды, дальше «Nм Sс».
 *  Нужна для времени прокачки: один профиль занимает 30–90 секунд, а
 *  formatDurationRu округлял всё это в «< 1 минуты» — цифра была бесполезной. */
// Человеческие подписи для типов кубиков в подсказке «на что уходит время».
// Незнакомый кубик покажет своё имя — белого списка тут нет специально: набор
// кубиков оператор меняет в редакторе, и захардкоженный список отстанет.
const PHASE_LABELS: Record<string, string> = {
  launch_browser: 'Запуск браузера',
  load_serp: 'Загрузка выдачи',
  solve_captcha: 'Решение капчи',
  idle_emulation: 'Эмуляция поведения',
  nav_click: 'Клик по навигации',
  click_header_nav: 'Клик по навигации',
  dismiss_popups: 'Закрытие попапов',
  dismiss_distribution: 'Закрытие промо',
  gdpr_dismiss: 'Баннер согласия',
  proxy_apply: 'Подключение прокси',
  proxy_fetch_url: 'Получение прокси',
  fp_generate: 'Генерация отпечатка',
  fp_apply: 'Применение отпечатка',
  profile_claim: 'Бронь профиля',
  profile_claim_pid: 'Бронь профиля',
  profile_restore_fields: 'Чтение профиля',
  restore_cookies: 'Восстановление cookies',
  save_export_cookies: 'Экспорт cookies',
  save_close_browser: 'Закрытие браузера',
  save_write_db: 'Запись в базу',
  count_profiles: 'Подсчёт пула',
  antidetect_selfcheck: 'Самопроверка антидетекта',
  warmup_serp_tune: 'Прогрев выдачи',
}

/** Спарклайн 2026-стиля: голый SVG, без библиотек и ресайз-наблюдателей.
 *  Даёт НАПРАВЛЕНИЕ рядом с цифрой (гайдлайн «значение + контекст + тренд»),
 *  полноценный график живёт ниже, в отдельной вкладке. */
function Sparkline({ data, className = '' }: { data: number[]; className?: string }) {
  if (!data || data.length < 3) return null
  const w = 60, h = 16
  const max = Math.max(...data, 1)
  const step = w / (data.length - 1)
  const pts = data.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`)
  const up = data[data.length - 1] >= data[data.length - 2]
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className={className} aria-hidden>
      <polyline points={pts.join(' ')} fill="none" strokeWidth={1.5}
                strokeLinecap="round" strokeLinejoin="round"
                className={up ? 'stroke-sky-500/80' : 'stroke-amber-500/80'} />
    </svg>
  )
}

/** Средние секунды на фазу за все итерации + доля от суммы. */
function buildPhaseRows(ps: any): Array<{ k: string; v: string; pct: number }> {
  const n = Number(ps?.n || 0)
  const sum = ps?.sum
  if (!n || !sum || typeof sum !== 'object') return []
  const rows = Object.entries(sum as Record<string, number>)
    .map(([k, total]) => ({ k, sec: Number(total) / n }))
    .filter(r => r.sec >= 0.1)
    .sort((a, b) => b.sec - a.sec)
    .slice(0, 8)
  const top = rows[0]?.sec || 1
  return rows.map(r => ({
    k: PHASE_LABELS[r.k] || r.k,
    v: r.sec < 10 ? `${r.sec.toFixed(1)} с` : `${Math.round(r.sec)} с`,
    pct: Math.max(4, Math.round((r.sec / top) * 100)),
  }))
}

function formatShortDur(sec: number): string {
  if (!sec || sec < 0) return '—'
  if (sec < 60) return `${sec < 10 ? sec.toFixed(1) : Math.round(sec)} с`
  const m = Math.floor(sec / 60)
  const s = Math.round(sec % 60)
  return s > 0 ? `${m} м ${s} с` : `${m} м`
}

function formatUptime(sec: number): string {
  if (sec < 60) return `${sec}s`
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return m < 60 ? `${m}m ${s}s` : `${Math.floor(m / 60)}h ${m % 60}m`
}

function formatStartedAt(ts: number): string {
  const elapsed = Math.max(0, Math.floor(Date.now() / 1000 - ts))
  return formatDurationRu(elapsed)
}

// ВАЖНО: состояние воркера в декларативном сценарии = ИМЯ ТИПА КУБИКА
// (scenario_engine ставит ctx.state(btype, ...)), а кубики оператор добавляет и
// переставляет в РЕДАКТОРЕ СЦЕНАРИЯ. Поэтому белый список «активных» состояний
// здесь всегда отставал: новый кубик → плитка становилась некликабельной и VNC
// было не открыть (например, на solve_captcha смотреть за потоком было нельзя —
// приходилось ждать конца капчи). Список ведём ТОЛЬКО для неактивных и
// стартовых фаз; всё остальное считаем активной работой: браузер жив, плитка
// кликабельна, VNC открывается. Новые кубики поддержки здесь не требуют.
function workerPhase(state: string): 'active' | 'starting' | 'idle' {
  if (STARTING_STATES.has(state)) return 'starting'
  if (INACTIVE_STATES.has(state)) return 'idle'
  return 'active'
}

const STATE_LABELS: Record<string, string> = {
  navigate: 'Загрузка страницы',
  generate_query: 'Подбор запроса',
  click_selector: 'Тап по элементу',
  type: 'Ввод запроса',
  press_key: 'Отправка запроса',
  wait_url: 'Ожидание адреса',
  wait_selector: 'Ожидание элемента',
  current_url: 'Проверка адреса',
  check_page_status: 'Проверка ошибки',
  click_random_serp: 'Переход на сайт',
  sleep: 'Пауза',
  running: 'Работает',
  launching: 'Запуск...',
  claiming: 'Получение профиля...',
  task_fetch: 'Загрузка задания...',
  fingerprint: 'Отпечаток...',
  proxy: 'Подключение прокси...',
  pause: 'Пауза',
  idle: 'Пауза',
  closing: 'Закрытие...',
  stopped: 'Остановлен',
  unknown: 'Ожидание...',
  completed: 'Завершён',
  waiting_profile: 'Ожидание профилей...',
  click_hour_limit: 'Лимит кликов в час — ждём след. час',
  waiting_db: 'Ожидание БД...',
  serp_fail_paused: '⏸ Пауза: выдача не загрузилась (VNC)',
  // Профайлерские warming-состояния (scenarios/warming.py → _state_writer)
  warming_init: 'Получение кук',
  serp_load: 'Загрузка SERP',
  popups_dismiss: 'Закрытие попапов',
  idle_emulation: 'Эмуляция поведения',
  nav_click: 'Клик HeaderNav',
  manual_vnc: '🖐 Ручное управление (VNC)',
  // Состояния = ИМЕНА КУБИКОВ редактора сценария (ctx.state(btype)). Здесь
  // только ПОДПИСИ для читаемости — на кликабельность плитки они не влияют
  // (см. workerPhase: активность определяется по списку НЕактивных фаз).
  // Незнакомый кубик просто покажет своё имя, но VNC останется доступным.
  init_cookies: 'Получение кук',
  load_serp: 'Загрузка выдачи',
  gdpr: 'Баннер согласия',
  dismiss_popups: 'Закрытие попапов',
  solve_captcha: 'Решение капчи',
  dismiss_distribution: 'Закрытие промо',
  click_header_nav: 'Клик по навигации',
  antidetect_selfcheck: 'Проверка антидетект-контуров',
  click_tune_button: 'Клик кнопки Tune',
  rewrite_link_click: 'Подмена ссылки + клик',
  type_search_query: 'Ввод запроса',
  warmup_serp_tune: 'Прогрев (Serp Tune)',
  seed_spravka: 'Посев spravka-куки',
  scroll: 'Скролл',
  wait: 'Пауза сценария',
  // Инфра-кубики (профиль / прокси / браузер / сохранение)
  db_ensure_table: 'Подготовка таблицы',
  count_profiles: 'Подсчёт пула',
  create_daily_gate: 'Суточная квота создания',
  profile_claim: 'Бронь профиля',
  profile_claim_pid: 'Бронь профиля',
  profile_restore_fields: 'Чтение профиля',
  profile_create: 'Создание профиля',
  fp_generate: 'Отпечаток: генерация',
  fp_apply: 'Отпечаток: применение',
  proxy_apply: 'Подключение прокси',
  launch_browser: 'Запуск браузера',
  cookies_prepare: 'Подготовка кук',
  cookies_restore: 'Восстановление кук',
  save_resolve_level: 'Сохранение: уровень',
  save_used_increment: 'Сохранение: счётчик',
  save_export_cookies: 'Сохранение: экспорт кук',
  save_close_browser: 'Сохранение: закрытие браузера',
  save_write_db: 'Сохранение: запись в БД',
}

export const ResultsStats = memo(function ResultsStats({ stats, mode = 'booster' }: { stats: any; mode?: 'booster' | 'profiler' }) {
  const ok = stats.ok ?? 0
  const fail = stats.fail ?? 0
  const skip = stats.skip ?? 0  // Idea #69: SmartPilot skips
  const total = ok + fail
  const avgPos = stats.avg_position ?? 0
  const speed = stats.speed_per_hour ?? 0
  const elapsed = stats.elapsed_sec ?? 0
  const captcha = stats.captcha ?? 0
  const captchaTypes: Record<string, number> = stats.captcha_types ?? {}
  // ВАЖНО: captchaTypes — это ЭТАПЫ одной капчи, а не независимые события.
  // Чекбокс почти всегда эскалирует в картинку, и воркер пишет обе строки —
  // итоговая так и говорит: «Капча пройдена. CheckBox + SmartCaptcha IMG».
  // Поэтому сумму чипов показывать НЕЛЬЗЯ (одна капча считалась бы дважды):
  // в плитке — число самих капч, в подписи — в скольких прокачках они были.
  const captchaEvents = (stats as any).captcha_events ?? 0
  const captchaTotal = captchaEvents || captcha
  const error429 = stats.error_429 ?? 0
  const totalTasks = stats.total_tasks ?? 0
  const remainingTasks = stats.remaining_tasks ?? 0
  const isProfiler = mode === 'profiler'
  // Per-iteration timing (profiler only) — avg/min/max seconds.
  const iterAvg = stats.iter_avg_sec ?? 0
  // Разбивка времени прокачки: приходит суммой по типам кубиков + числом
  // итераций, средние считаем здесь — сервер ничего дополнительно не считает.
  const spark: number[] = ((stats as any).spark as number[]) || []
  const phaseRows = useMemo(() => buildPhaseRows((stats as any).phase_stats),
                            [(stats as any).phase_stats])
  const iterMin = stats.iter_min_sec ?? 0
  const iterMax = stats.iter_max_sec ?? 0
  // Состав пачки профилей (профайлер): размер, средний уровень, средний
  // возраст. Считается на сервере раз в час одним запросом — здесь только
  // показываем, поэтому обновление раз в минуту вместе со «медленным» WS.
  const pool = stats.pool_stats as
    | { count?: number; avg_level?: number; avg_age_hours?: number; ts?: number }
    | undefined
  const poolAgeH = pool?.avg_age_hours ?? 0
  const poolAge = poolAgeH >= 48
    ? `${(poolAgeH / 24).toFixed(1)} дн`
    : `${Math.round(poolAgeH)} ч`

  const pct = total > 0 ? Math.round(ok / total * 100) : 0
  const tasksPct = totalTasks > 0 ? Math.round(((totalTasks - remainingTasks) / totalTasks) * 100) : 0

  // Кольцо успешности: тон ведёт взгляд раньше цифр — зелёный «всё ровно»,
  // янтарный «стоит посмотреть», алый «разбираться сейчас».
  const ringTone = total === 0 ? 'muted' : pct >= 95 ? 'ok' : pct >= 80 ? 'warn' : 'bad'
  const RING = {
    ok: { stroke: 'stroke-emerald-500', text: 'text-emerald-500 dark:text-emerald-400', glow: 'shadow-[0_0_28px_-12px_rgb(16_185_129/0.6)]' },
    warn: { stroke: 'stroke-amber-500', text: 'text-amber-500 dark:text-amber-400', glow: 'shadow-[0_0_28px_-12px_rgb(245_158_11/0.6)]' },
    bad: { stroke: 'stroke-rose-500', text: 'text-rose-500 dark:text-rose-400', glow: 'shadow-[0_0_28px_-12px_rgb(244_63_94/0.6)]' },
    muted: { stroke: 'stroke-muted-foreground/30', text: 'text-muted-foreground', glow: '' },
  }[ringTone]
  const R = 30
  const CIRC = 2 * Math.PI * R

  // Вторичные показатели. Для профайлера позиция в выдаче бессмысленна (он не
  // кликает по результатам) — вместо неё среднее время прокачки.
  const metrics: Array<{ icon: any; label: string; value: React.ReactNode; hint?: string; accent?: string; detail?: Array<{ k: string; v: string; pct: number }> }> = [
    isProfiler
      ? {
          icon: Timer,
          label: 'Прокачка профиля',
          value: iterAvg > 0 ? formatShortDur(iterAvg) : '—',
          hint: iterAvg > 0 && iterMax > 0 ? `от ${formatShortDur(iterMin)} до ${formatShortDur(iterMax)}` : 'среднее на профиль',
          detail: phaseRows.length > 0 ? phaseRows : undefined,
        }
      : {
          icon: Crosshair,
          label: 'AVG позиция',
          value: avgPos > 0 ? avgPos : '—',
          hint: 'без бренда',
        },
    {
      icon: Gauge,
      label: isProfiler ? 'Прогревов/ч' : 'Скорость',
      value: speed > 0 ? (
        <span className="flex items-baseline gap-2">
          <span>{speed.toLocaleString('ru-RU')}</span>
          <Sparkline data={spark} className="opacity-80" />
        </span>
      ) : '—',
      hint: speed > 0 ? `≈${(speed * 24).toLocaleString('ru-RU')} в сутки` : 'нет данных',
      accent: speed > 0 ? 'text-sky-500 dark:text-sky-400' : undefined,
    },
    {
      icon: ShieldAlert,
      label: 'Капча',
      value: captchaTotal.toLocaleString('ru-RU'),
      hint: captchaTotal > 0 ? (captcha > 0 ? `в ${captcha.toLocaleString('ru-RU')} ${isProfiler ? 'прокачках' : 'заданиях'}` + (total > 0 ? ` (${Math.round((captcha / total) * 100)}%)` : '') : `${Object.keys(captchaTypes).length} типов`) : 'не встречалась',
      accent: captchaTotal > 0 ? 'text-amber-500 dark:text-amber-400' : undefined,
    },
    {
      icon: Clock3,
      label: 'В работе',
      value: elapsed > 0 ? formatElapsed(elapsed) : '—',
      hint: total > 0 ? `${total.toLocaleString('ru-RU')} итераций` : 'ожидание',
    },
    ...(!isProfiler && skip > 0 ? [{
      icon: SkipForward, label: 'Пропуски', value: skip.toLocaleString('ru-RU'), hint: 'SmartPilot',
    }] : []),
    ...(totalTasks > 0 ? [{
      icon: ListChecks,
      label: 'Осталось',
      value: totalTasks === -1 ? '∞' : remainingTasks.toLocaleString('ru-RU'),
      hint: totalTasks === -1 ? 'без лимита' : `${tasksPct}% выполнено`,
    }] : []),
  ]

  return (
    <div data-no-magic className="mb-5 animate-fade-in">
      {/* Ровный фон без градиента: на тёмной теме диагональный переход давал
          грязную полосу поперёк карточки. Секции разделяем границами. */}
      <div className="relative z-20 rounded-2xl border border-border/70 bg-card">

        <div className="flex flex-col lg:flex-row">
          {/* Главный показатель: доля успеха кольцом + разбивка */}
          <div className="flex items-center gap-4 p-4 md:p-5 lg:w-[300px] lg:shrink-0 lg:border-r border-border/60">
            <div className={`relative shrink-0 rounded-full ${RING.glow}`}>
              <svg width="76" height="76" viewBox="0 0 76 76" className="-rotate-90">
                <circle cx="38" cy="38" r={R} className="stroke-muted-foreground/15" strokeWidth="6" fill="none" />
                <circle
                  cx="38" cy="38" r={R} strokeWidth="6" fill="none" strokeLinecap="round"
                  className={`${RING.stroke} transition-[stroke-dasharray] duration-700 ease-out`}
                  strokeDasharray={`${(CIRC * pct) / 100} ${CIRC}`}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-lg font-bold tabular-nums leading-none ${RING.text}`}>
                  {total > 0 ? `${pct}%` : '—'}
                </span>
                <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70 mt-0.5">успех</span>
              </div>
            </div>

            <div className="min-w-0">
              <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">Выполнений</p>
              <div className="text-3xl font-bold tabular-nums tracking-tight leading-tight">
                {total.toLocaleString('ru-RU')}
              </div>
              <div className="flex items-center gap-3 mt-1.5">
                <span className="inline-flex items-center gap-1 text-[11px]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
                  <span className="tabular-nums font-semibold">{ok.toLocaleString('ru-RU')}</span>
                </span>
                <span className={`inline-flex items-center gap-1 text-[11px] ${fail > 0 ? '' : 'opacity-50'}`}>
                  <XCircle className={`w-3.5 h-3.5 ${fail > 0 ? 'text-rose-500 dark:text-rose-400' : 'text-muted-foreground'}`} />
                  <span className="tabular-nums font-semibold">{fail.toLocaleString('ru-RU')}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Вторичные показатели */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 flex-1 border-t lg:border-t-0 border-border/60">
            {metrics.map((m, i) => {
              const Icon = m.icon
              return (
                <div
                  key={m.label}
                  className={`group/metric relative px-3.5 py-3 md:py-4 min-w-0 transition-colors hover:bg-muted/40 ${
                    i % 2 !== 0 ? 'border-l border-border/60' : ''
                  } sm:${i % 3 !== 0 ? 'border-l' : ''} ${i >= 2 ? 'border-t border-border/60 sm:border-t-0' : ''} ${
                    i >= 3 ? 'lg:border-t-0' : ''
                  }`}
                >
                  <p className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70 mb-1">
                    <Icon className="w-3 h-3 shrink-0" />
                    <span className="truncate">{m.label}</span>
                  </p>
                  <div className={`text-lg md:text-xl font-bold tabular-nums tracking-tight leading-tight ${m.accent || ''}`}>
                    {m.value}
                  </div>
                  <p className="text-[10px] text-muted-foreground/80 mt-0.5 truncate">{m.hint}</p>
                  {m.detail && (
                    // Прогрессивное раскрытие: сводка на плитке, детали по
                    // наведению. Данные уже пришли с состоянием, никаких
                    // дозапросов при открытии подсказки не делается.
                    // Ширина ФИКСИРОВАННАЯ, не по плитке: плитка узкая, и три колонки
                    // (подпись + полоса + значение) в неё не влезали — значения выходили
                    // за фон. Полоса теперь подложка строки, колонок две, переполниться
                    // нечему.
                    <div className="pointer-events-none absolute left-0 top-full z-50 mt-1.5 hidden w-[264px] max-w-[calc(100vw-2rem)] group-hover/metric:block">
                      <div className="overflow-hidden rounded-xl border border-border/70 bg-popover shadow-2xl">
                        <p className="border-b border-border/50 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                          На что уходит время
                        </p>
                        <div className="p-1.5">
                          {m.detail.map(d => (
                            <div key={d.k} className="relative flex items-center gap-2 rounded-md px-1.5 py-[3px]">
                              <span className="absolute inset-y-0 left-0 rounded-md bg-sky-500/10 dark:bg-sky-400/15"
                                    style={{ width: `${d.pct}%` }} />
                              <span className="relative min-w-0 flex-1 truncate text-[11px] leading-5 text-muted-foreground">
                                {d.k}
                              </span>
                              <span className="relative shrink-0 text-[11px] leading-5 font-semibold tabular-nums">
                                {d.v}
                              </span>
                            </div>
                          ))}
                        </div>
                        <p className="border-t border-border/50 px-3 py-1.5 text-[10px] leading-4 text-muted-foreground/60">
                          среднее за итерацию
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Состав пачки профилей — срез с сервера раз в час */}
        {isProfiler && pool && (pool.count ?? 0) > 0 && (
          <div className="border-t border-border/60 px-3.5 py-2.5 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60 mr-1">
              <Layers className="w-3 h-3" /> Пачка
            </span>
            {[
              { k: 'профилей', v: (pool.count ?? 0).toLocaleString('ru-RU') },
              { k: 'ср. уровень', v: (pool.avg_level ?? 0).toFixed(2) },
              { k: 'ср. возраст', v: poolAge },
            ].map(x => (
              <div key={x.k} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-muted/50 border border-border/50 text-[11px]">
                <span className="text-muted-foreground">{x.k}</span>
                <span className="tabular-nums font-bold">{x.v}</span>
              </div>
            ))}
            {pool.ts ? (
              <span className="text-[10px] text-muted-foreground/50 ml-auto tabular-nums">
                срез {new Date(pool.ts * 1000).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
              </span>
            ) : null}
          </div>
        )}

        {/* Типы капчи и сетевые отказы */}
        {(captcha > 0 || error429 > 0) && (
          <div className="border-t border-border/60 px-3.5 py-2.5 flex items-center gap-2 flex-wrap">
            {captchaTotal > 0 && Object.keys(captchaTypes).length > 0 && (
              <>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60 mr-1"
                      title="Этапы, через которые проходила капча. Одна капча может дать несколько этапов (чекбокс → картинка), поэтому суммировать их нельзя.">
                  <ShieldAlert className="w-3 h-3" /> Этапы капчи
                </span>
                {Object.entries(captchaTypes).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
                  <div key={name} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/20 text-[11px]">
                    <span className="text-muted-foreground truncate">{CAPTCHA_LABELS[name] || name}</span>
                    <span className="tabular-nums font-bold text-amber-600 dark:text-amber-400">{count}</span>
                  </div>
                ))}
              </>
            )}
            {error429 > 0 && (
              <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-orange-500/10 border border-orange-500/20 text-[11px] ${captcha > 0 ? 'ml-auto' : ''}`}>
                <span className="text-orange-500 dark:text-orange-400">Error 429</span>
                <span className="tabular-nums font-bold text-orange-600 dark:text-orange-300">{error429}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
})

const INACTIVE_STATES = new Set(['idle', 'pause', 'closing', 'stopped', 'claiming', 'task_fetch', 'fingerprint', 'proxy', 'launching', 'unknown', 'completed', 'waiting_profile', 'waiting_db', 'api_no_task', 'proxy_wait', 'click_hour_limit'])
// Фазы запуска (браузера ещё НЕТ) — плитка светится янтарным, VNC не нужен:
// смотреть там нечего, дисплей пустой. Кроме legacy-имён сюда входят инфра-
// кубики редактора, которые по шаблону идут ДО LaunchBrowser (пул → профиль →
// отпечаток → прокси → запуск). Проверяется ПЕРВЫМ (см. workerPhase).
const STARTING_STATES = new Set([
  'launching', 'claiming', 'fingerprint', 'proxy', 'task_fetch',
  'db_ensure_table', 'count_profiles', 'create_daily_gate',
  'profile_claim', 'profile_claim_pid', 'profile_restore_fields', 'profile_create',
  'fp_generate', 'fp_apply', 'proxy_apply', 'launch_browser',
])
const PADDING = 4
const TITLEBAR_H = 26
const VNC_SCALE = 0.85
const MAX_W = 480

function VncViewer({ workerId, display, browserState, onClose }: {
  workerId: number; display: string; browserState: string; onClose: () => void
}) {
  // Multi-campaign safe: server-side VNC, screenshot and WS endpoints
  // are now keyed by absolute Xvfb display number (e.g. 149), not by
  // worker_id (which collides between concurrent campaigns). Parse it
  // out of the `display` string (":149" → 149) and use it for every
  // backend call. `workerId` stays only for the visible "W2" label.
  const displayNum = useMemo(() => {
    const m = /^:?(\d+)$/.exec((display || '').trim())
    return m ? parseInt(m[1], 10) : (99 + workerId)
  }, [display, workerId])
  const inactive = INACTIVE_STATES.has(browserState)
  const [mode, setMode] = useState<'loading' | 'vnc' | 'screenshot' | 'error'>('loading')
  const [interactive, setInteractive] = useState(false)
  const [screenW, setScreenW] = useState(385)
  const [screenH, setScreenH] = useState(854)
  const [xvfbW, setXvfbW] = useState(398)
  const [xvfbH, setXvfbH] = useState(815)
  const [errMsg, setErrMsg] = useState('')
  const closedRef = useRef(false)
  const [disconnected, setDisconnected] = useState(false)
  const [imgUrl, setImgUrl] = useState('')
  const [imgLoading, setImgLoading] = useState(false)
  const [viewportW, setViewportW] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024))
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const prevStateRef = useRef(browserState)
  const preferVnc = useRef(true)
  const rfbRef = useRef<any>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const intentionalRef = useRef(false)
  // Окно-вставка (fallback, когда navigator.clipboard недоступен — http/нет прав).
  const [pasteOpen, setPasteOpen] = useState(false)
  const pasteRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const layout = useMemo(() => {
    const ratio = xvfbW / xvfbH
    const sidePad = 16
    const maxContentW = Math.min(MAX_W, Math.max(120, viewportW - sidePad - PADDING * 2))
    let h = window.innerHeight * VNC_SCALE - TITLEBAR_H - PADDING * 2
    let w = h * ratio
    if (w > maxContentW) { w = maxContentW; h = w / ratio }
    return {
      frameW: Math.round(w + PADDING * 2),
      frameH: Math.round(h + TITLEBAR_H + PADDING * 2),
      contentW: Math.round(w),
      contentH: Math.round(h),
      scale: w / xvfbW,
    }
  }, [xvfbW, xvfbH, viewportW])

  const applyDims = (r: any) => {
    if (r.screen_w) setScreenW(r.screen_w)
    if (r.screen_h) setScreenH(r.screen_h)
    if (r.xvfb_w) setXvfbW(r.xvfb_w)
    if (r.xvfb_h) setXvfbH(r.xvfb_h)
  }

  const destroyRfb = useCallback(() => {
    intentionalRef.current = true
    try { rfbRef.current?.disconnect() } catch {}
    rfbRef.current = null
  }, [])

  const connectRfb = useCallback((pw: string, viewOnly: boolean) => {
    destroyRfb()
    const el = containerRef.current
    if (!el) return
    const proto = location.protocol === 'https:' ? 'wss' : 'ws'
    const url = `${proto}://${location.hostname}${location.port ? ':' + location.port : ''}/ws/vnc/${displayNum}`
    // Bug #118: retry VNC module load once — network hiccups or stale
    // cache on client VPS can cause the lazy chunk to fail to load.
    const loadRfb = () => import('@novnc/novnc/lib/rfb.js')
    loadRfb().catch(() => new Promise(r => setTimeout(r, 2000)).then(loadRfb)).then(mod => {
      if (closedRef.current) return
      const RFB = mod.default
      const rfb = new RFB(el, url, { credentials: pw ? { password: pw } : undefined, wsProtocols: ['binary'] })
      rfb.viewOnly = viewOnly
      rfb.scaleViewport = true
      rfb.resizeSession = false
      rfb.clipViewport = true
      rfb.addEventListener('connect', () => {
        if (!closedRef.current) { setDisconnected(false); setMode('vnc') }
      })
      rfb.addEventListener('disconnect', () => {
        if (closedRef.current) return
        if (intentionalRef.current) { intentionalRef.current = false; return }
        setDisconnected(true)
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = setTimeout(() => {
          if (!closedRef.current && preferVnc.current) reconnect()
        }, 1500)
      })
      // Буфер VNC → Mac: копирование в удалённом браузере (Ctrl+C) → x11vnc шлёт
      // ServerCutText → noVNC событие 'clipboard' → кладём в локальный буфер Mac.
      // Требует secure context (https) для navigator.clipboard.writeText.
      rfb.addEventListener('clipboard', (e: any) => {
        const t = e?.detail?.text
        if (t && navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(t).catch(() => { /* нет прав / insecure http */ })
        }
      })
      rfbRef.current = rfb
    }).catch(() => {
      if (!closedRef.current) { setErrMsg('Failed to load VNC module'); setMode('error') }
    })
  }, [displayNum])

  // Дисплей воркера меняет размер между профилями (browser.py ужимает Xvfb
  // под окно, x11vnc -xrandr шлёт новый размер кадра). Размеры из startVnc
  // берутся один раз при открытии — подтягиваем модалку к реальному кадру,
  // иначе при другой пропорции по краям видна подложка контейнера.
  useEffect(() => {
    if (mode !== 'vnc') return
    const t = setInterval(() => {
      const r = rfbRef.current
      const fw = r?._fbWidth, fh = r?._fbHeight
      if (fw > 0 && fh > 0) {
        setXvfbW(prev => (prev === fw ? prev : fw))
        setXvfbH(prev => (prev === fh ? prev : fh))
      }
    }, 1500)
    return () => clearInterval(t)
  }, [mode])

  useEffect(() => {
    closedRef.current = false
    let cancelled = false
    ;(async () => {
      const res = await startVnc(displayNum)
      if (cancelled || closedRef.current) return
      applyDims(res)
      if (res.vnc_port) {
        setInteractive(!!res.interactive)
        connectRfb(res.vnc_pw || '', !res.interactive)
      } else {
        setErrMsg(res.error || 'Трансляция недоступна')
        setImgUrl(screenshotUrl(displayNum))
        setMode('screenshot')
      }
    })()
    return () => { cancelled = true; destroyRfb() }
  }, [displayNum, connectRfb, destroyRfb])

  const handleClose = useCallback(() => {
    closedRef.current = true
    destroyRfb()
    if (intervalRef.current) clearInterval(intervalRef.current)
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
    stopVnc(displayNum)
    onClose()
  }, [displayNum, onClose, destroyRfb])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [handleClose])

  const refreshScreenshot = useCallback(() => {
    setImgLoading(true)
    setImgUrl(screenshotUrl(displayNum))
  }, [displayNum])

  useEffect(() => {
    if (mode !== 'screenshot') return
    intervalRef.current = setInterval(refreshScreenshot, 2000)
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [mode, refreshScreenshot])

  const switchToScreenshot = useCallback(() => {
    preferVnc.current = false
    destroyRfb()
    stopVnc(displayNum)
    setImgUrl(screenshotUrl(displayNum))
    setMode('screenshot')
  }, [displayNum, destroyRfb])

  const reconnect = useCallback(async () => {
    preferVnc.current = true
    destroyRfb()
    if (intervalRef.current) clearInterval(intervalRef.current)
    setDisconnected(false)
    setMode('loading')
    const res = await startVnc(displayNum)
    if (closedRef.current) return
    applyDims(res)
    if (res.vnc_port) {
      setInteractive(!!res.interactive)
      connectRfb(res.vnc_pw || '', !res.interactive)
    } else {
      setErrMsg(res.error || 'Трансляция недоступна')
      setImgUrl(screenshotUrl(displayNum))
      setMode('screenshot')
    }
  }, [displayNum, connectRfb, destroyRfb])

  useEffect(() => {
    const prev = prevStateRef.current
    prevStateRef.current = browserState
    if (browserState === 'running' && prev !== 'running' && !closedRef.current && preferVnc.current && mode !== 'vnc') {
      setMode('loading')
      const t = setTimeout(() => { if (!closedRef.current) reconnect() }, 1200)
      return () => clearTimeout(t)
    }
  }, [browserState, reconnect, mode])

  // Отправить текст в СФОКУСИРОВАННОЕ поле удалённого браузера: печатаем его
  // посимвольно через sendKey — это НАДЁЖНО и НЕ зависит от буфера удалённого X
  // и обработки Ctrl+V (которые могут не сработать). Дополнительно кладём текст в
  // буфер remote (clipboardPasteFrom), чтобы работал и ручной Ctrl+V. Unicode —
  // через -xkb/-add_keysyms в x11vnc.
  const writeToRemote = useCallback((text: string) => {
    const rfb = rfbRef.current
    if (!rfb || !text) return
    try { rfb.clipboardPasteFrom(text) } catch { /* для ручного Ctrl+V */ }
    for (const ch of text) {
      const cp = ch.codePointAt(0) || 0
      let keysym: number
      if (ch === '\n' || ch === '\r') keysym = 0xff0d       // Return
      else if (ch === '\t') keysym = 0xff09                 // Tab
      else if (cp < 0x100) keysym = cp                      // Latin-1 (ASCII)
      else keysym = 0x01000000 + cp                         // Unicode keysym
      try {
        rfb.sendKey(keysym, null, true)
        rfb.sendKey(keysym, null, false)
      } catch { /* sendKey недоступен */ }
    }
  }, [])

  const pasteToVnc = useCallback(async () => {
    // 1) Прямое чтение буфера Mac (нужен https + разрешение) → печать в поле.
    try {
      if (navigator.clipboard?.readText) {
        const text = await navigator.clipboard.readText()
        if (text) { writeToRemote(text); return }
      }
    } catch { /* нет прав / insecure http — уходим в окно-вставку ниже */ }
    // 2) Fallback: окно-вставка. Нативный ⌘V в textarea работает ВЕЗДЕ (в т.ч.
    //    http, без clipboard API) — оттуда печатаем в удалённое поле.
    setPasteOpen(true)
    setTimeout(() => pasteRef.current?.focus(), 30)
  }, [writeToRemote])

  const submitPasteBox = useCallback(() => {
    const t = pasteRef.current?.value || ''
    setPasteOpen(false)
    if (t) writeToRemote(t)
  }, [writeToRemote])

  // Ремап Mac-комбинаций (⌘) в удалённые Ctrl-комбинации внутри VNC: ⌘A/⌘C/⌘V/
  // ⌘X/⌘Z/⌘Y/⌘⇧Z → Ctrl+… в Linux-браузере (выделить всё / копировать / вставить
  // / вырезать / отменить / повторить). На Mac это Cmd(⌘), а remote-браузер ждёт
  // Ctrl — без ремапа ⌘A/⌘C не работают. ⌘V дополнительно синкает буфер Mac→remote
  // и жмёт Ctrl+V (см. pasteToVnc). Настоящий Ctrl с внешней клавиатуры noVNC
  // прокидывает сам — не трогаем. Немапленные ⌘-комбо (⌘W/⌘R/⌘Tab) отдаём Mac.
  // capture=true — перехватываем ДО noVNC, чтобы в remote не ушёл лишний Meta+key.
  useEffect(() => {
    // Пока открыто окно-вставка — НЕ перехватываем комбинации: пользователь
    // работает с нативной textarea (⌘V/⌘A/⌘C в ней должны работать штатно).
    if (mode !== 'vnc' || !interactive || pasteOpen) return
    const CTRL = 0xffe3
    const COMBO: Record<string, [number, string]> = {
      a: [0x61, 'KeyA'], c: [0x63, 'KeyC'], v: [0x76, 'KeyV'],
      x: [0x78, 'KeyX'], z: [0x7a, 'KeyZ'], y: [0x79, 'KeyY'],
    }
    const handler = (e: KeyboardEvent) => {
      // Только Cmd(⌘) без Ctrl/Alt. Реальный Ctrl noVNC прокидывает сам.
      if (!e.metaKey || e.ctrlKey || e.altKey) return
      let k = e.key.toLowerCase()
      if (k === 'z' && e.shiftKey) k = 'y'  // ⌘⇧Z (redo на Mac) → Ctrl+Y
      const combo = COMBO[k]
      if (!combo) return  // немапленные ⌘-комбо → Mac-браузеру
      e.preventDefault(); e.stopPropagation()
      if (k === 'v') { pasteToVnc(); return }  // синк буфера Mac→remote + Ctrl+V
      const rfb = rfbRef.current
      if (!rfb) return
      try {
        rfb.sendKey(CTRL, 'ControlLeft', true)
        rfb.sendKey(combo[0], combo[1], true)
        rfb.sendKey(combo[0], combo[1], false)
        rfb.sendKey(CTRL, 'ControlLeft', false)
      } catch { /* sendKey недоступен в этой сборке noVNC */ }
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [mode, interactive, pasteToVnc, pasteOpen])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-2 md:px-0" onClick={handleClose}>
      <div className="relative flex flex-col animate-slide-up max-w-[calc(100vw-1rem)] md:max-w-none" style={{ width: layout.frameW, height: layout.frameH }} onClick={e => e.stopPropagation()}>
        <div className="flex flex-col h-full rounded-2xl shadow-2xl overflow-hidden bg-card border-2 border-border">
          <div className="flex items-center justify-between px-3 shrink-0 bg-muted border-b border-border" style={{ height: TITLEBAR_H }}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs font-semibold text-foreground">W{workerId + 1}</span>
              <span className="text-[9px] font-mono truncate text-muted-foreground">{screenW}×{screenH}</span>
              {inactive
                ? <span className="text-[9px] text-muted-foreground">{STATE_LABELS[browserState] || browserState}</span>
                : mode === 'vnc' ? <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 pulse-glow" />
                : mode === 'screenshot' ? <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" /> : null}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {mode === 'screenshot' && <Button variant="ghost" size="sm" onClick={reconnect} className="h-5 px-1.5 text-[9px] text-blue-400 hover:text-blue-300">LIVE</Button>}
              {mode === 'vnc' && <>
                {interactive && <Button variant="ghost" size="icon" onClick={pasteToVnc} className="h-5 w-5 text-muted-foreground hover:text-emerald-400" title="Вставить из буфера (Ctrl+V)"><ClipboardPaste className="w-3 h-3" /></Button>}
                <Button variant="ghost" size="icon" onClick={reconnect} className="h-5 w-5 text-muted-foreground hover:text-blue-400" title="Переподключить"><RefreshCw className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" onClick={switchToScreenshot} className="h-5 px-1.5 text-[9px] text-muted-foreground">IMG</Button>
              </>}
              <Button variant="ghost" size="icon" onClick={handleClose} className="h-5 w-5 text-sm text-muted-foreground">×</Button>
            </div>
          </div>

          <div className="relative bg-black overflow-hidden" style={{ width: layout.contentW, height: layout.contentH, cursor: interactive && mode === 'vnc' ? TOUCH_CURSOR_CSS : undefined }}>
            {/* noVNC renders into this container */}
            <div ref={containerRef} style={{ width: layout.contentW, height: layout.contentH, position: 'absolute', top: 0, left: 0 }} />
            {pasteOpen && (
              <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-black/85 backdrop-blur-sm p-4" onClick={e => e.stopPropagation()}>
                <div className="text-[11px] text-foreground/90 text-center leading-snug">
                  Вставьте данные сюда (<b>⌘V</b>) и нажмите «Вставить в браузер».<br />
                  Текст будет напечатан в поле, которое сейчас в фокусе в удалённом браузере.
                </div>
                <textarea ref={pasteRef} rows={4} autoFocus
                  className="w-full max-w-md text-xs rounded-md bg-background border border-border p-2 text-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="⌘V сюда…"
                  onKeyDown={e => {
                    if (e.key === 'Escape') { e.preventDefault(); setPasteOpen(false) }
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submitPasteBox() }
                  }} />
                <div className="flex items-center gap-2">
                  <Button size="sm" className="h-7 text-xs" onClick={submitPasteBox}>Вставить в браузер</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPasteOpen(false)}>Отмена</Button>
                </div>
              </div>
            )}
            {mode === 'loading' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 z-10">
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-muted-foreground">Подключение...</span>
              </div>
            )}
            {disconnected && mode === 'vnc' && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm transition-opacity duration-300">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-2" />
                <span className="text-xs text-muted-foreground">Переподключение...</span>
              </div>
            )}
            {mode === 'screenshot' && (
              <div className="w-full h-full flex items-start justify-center overflow-hidden">
                {imgUrl
                  ? <img src={imgUrl} alt={`W${workerId + 1}`} className={`w-full h-full object-contain transition-opacity duration-150 ${imgLoading ? 'opacity-40' : 'opacity-100'}`} onLoad={() => setImgLoading(false)} onError={() => { setImgUrl(''); setMode('error') }} draggable={false} />
                  : <span className="text-xs mt-20 text-muted-foreground/50">Loading...</span>}
              </div>
            )}
            {mode === 'error' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <span className="text-xs text-muted-foreground">{errMsg || 'Display unavailable'}</span>
                <Button variant="secondary" size="sm" onClick={reconnect} className="text-xs">Retry</Button>
              </div>
            )}
            {inactive && mode !== 'loading' && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm transition-opacity duration-300">
                <div className="text-sm font-medium mb-1 text-foreground">
                  {browserState === 'idle' || browserState === 'pause' ? 'Пауза' : browserState === 'claiming' ? 'Получение профиля...' : browserState === 'task_fetch' ? 'Загрузка задания...' : browserState === 'fingerprint' ? 'Отпечаток...' : browserState === 'proxy' ? 'Подключение прокси...' : browserState === 'launching' ? 'Запуск браузера...' : browserState === 'closing' ? 'Закрытие браузера...' : browserState === 'completed' ? 'Завершён' : browserState === 'waiting_profile' ? 'Ожидание профилей...' : browserState === 'waiting_db' ? 'Ожидание БД...' : 'Ожидание...'}
                </div>
                <div className="text-xs text-muted-foreground">Браузер не активен</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function WorkersTable({ workers, startedAt }: { workers: any[]; startedAt?: number }) {
  // Длительность текущего шага считаем от метки времени состояния, поэтому
  // нужен собственный тик: иначе цифра замрёт между обновлениями списка.
  const [nowSec, setNowSec] = useState(() => Date.now() / 1000)
  useEffect(() => {
    const t = setInterval(() => setNowSec(Date.now() / 1000), 1000)
    return () => clearInterval(t)
  }, [])
  const [selectedWorker, setSelectedWorker] = useState<any>(null)
  // Collapsed by default on mobile (< md breakpoint, 768px)
  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768
  )
  const [, setTick] = useState(0)

  const alive = workers.filter(w => w.alive)
  const dead = workers.length - alive.length
  const active = alive.filter(w => workerPhase(w.browser_state) === 'active').length
  const waiting = alive.length - active

  useEffect(() => {
    if (!startedAt) return
    const iv = setInterval(() => setTick(t => t + 1), 10000)
    return () => clearInterval(iv)
  }, [startedAt])

  useEffect(() => {
    if (selectedWorker && !workers.find(w => w.worker_id === selectedWorker.worker_id && w.alive))
      setSelectedWorker(null)
  }, [workers, selectedWorker])

  if (alive.length === 0 && dead === 0) return null

  return (
    <div>
      <div className="flex flex-col gap-2 mb-4 min-w-0 md:flex-row md:flex-wrap md:items-center md:gap-2.5">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <div className="flex items-center gap-1.5 shrink-0 md:gap-2">
            <h3 className="text-xs font-semibold text-foreground md:text-sm">Потоки</h3>
            <span className="inline-flex items-center h-5 px-1.5 text-[10px] font-bold tabular-nums rounded-md bg-muted border border-border/50 text-foreground md:h-[22px] md:px-2 md:text-[11px]">
              {active}<span className="text-muted-foreground/50 mx-0.5">/</span>{workers.length}
            </span>
          </div>

          {startedAt ? (
            <div className="flex items-center gap-1 px-1.5 h-5 min-w-0 rounded-md bg-muted/60 border border-border/40 md:gap-1.5 md:px-2 md:h-[22px]">
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-muted-foreground/60 md:w-[11px] md:h-[11px]">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
              <span className="hidden text-[10px] text-muted-foreground/60 md:inline md:text-[11px]">Время работы:</span>
              <span className="text-[10px] tabular-nums font-medium text-muted-foreground truncate md:text-[11px]">{formatStartedAt(startedAt)}</span>
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-1 min-w-0 md:gap-1.5">
          {active > 0 && (
            <span className="inline-flex items-center gap-0.5 h-5 px-1.5 text-[10px] font-medium rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 md:gap-1 md:h-[22px] md:px-2 md:text-[11px]">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
              <span className="truncate">{active} в работе</span>
            </span>
          )}
          {waiting > 0 && (
            <span className="inline-flex items-center gap-0.5 h-5 px-1.5 text-[10px] font-medium rounded-md bg-muted text-muted-foreground border border-border/40 md:gap-1 md:h-[22px] md:px-2 md:text-[11px]">
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
              <span className="truncate">{waiting} ожидание</span>
            </span>
          )}
          {dead > 0 && (
            <span className="inline-flex items-center gap-0.5 h-5 px-1.5 text-[10px] font-medium rounded-md bg-red-500/10 text-red-500 dark:text-red-400 border border-red-500/20 md:gap-1 md:h-[22px] md:px-2 md:text-[11px]">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500/60 shrink-0" />
              <span className="truncate">{dead} остановлено</span>
            </span>
          )}
        </div>

        <Button variant="ghost" size="sm" onClick={() => setCollapsed(!collapsed)}
          className="h-6 self-start text-[10px] text-muted-foreground/60 hover:text-muted-foreground gap-1 px-1.5 md:ml-auto md:self-center md:text-[11px] md:px-2">
          {collapsed
            ? <><Eye className="w-3 h-3 shrink-0" /> Показать потоки</>
            : <><EyeOff className="w-3 h-3 shrink-0" /> Скрыть потоки</>}
        </Button>
      </div>

      {collapsed ? null : alive.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-2">
          {alive.map(w => {
            const phase = workerPhase(w.browser_state)
            const isActive = phase === 'active'
            const isStarting = phase === 'starting'
            const dot = isActive ? 'bg-emerald-400 pulse-glow' : isStarting ? 'bg-amber-400 animate-pulse' : 'bg-muted-foreground'
            return (
              <Card key={w.worker_id}
                onClick={() => isActive ? setSelectedWorker(w) : null}
                className={`${isActive ? 'cursor-pointer hover:shadow-md hover:border-primary/40' : 'opacity-60'} transition-all duration-150 animate-fade-in ${isActive ? 'border-green-500/30 bg-green-500/5' : isStarting ? 'border-amber-500/20 bg-amber-500/5' : ''}`}>
                <CardContent className="px-2 py-2 text-xs md:px-3 md:py-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-semibold text-sm text-foreground">W{w.worker_id + 1}</span>
                    <div className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                  </div>
                  <div className="space-y-0.5 text-xs leading-tight">
                    <div className={isActive ? 'text-green-500/80' : isStarting ? 'text-amber-500/80' : 'text-muted-foreground'}>
                      {STATE_LABELS[w.browser_state] || w.browser_state || STATE_LABELS.unknown}
                    </div>
                    {w.state_detail?.step && <div className="truncate text-foreground">{w.state_detail.step}</div>}
                    {w.state_detail?.profile && <div className="truncate text-muted-foreground">{w.state_detail.profile}</div>}
                    <div className="tabular-nums text-muted-foreground" title={`в работе ${formatUptime(w.uptime_sec)}`}>{w.state_detail?.ts ? formatShortDur(Math.max(0, nowSec - w.state_detail.ts)) : formatUptime(w.uptime_sec)}</div>
                    {w.respawns > 0 && <div className="text-amber-500 tabular-nums">↻ {w.respawns}</div>}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      ) : (
        <div className="text-xs py-4 text-center rounded-lg border border-dashed border-border text-muted-foreground">
          Все потоки остановлены
        </div>
      )}

      {selectedWorker && (() => {
        const w = workers.find(x => x.worker_id === selectedWorker.worker_id)
        return (
          <VncViewer
            workerId={selectedWorker.worker_id}
            display={selectedWorker.display}
            browserState={w?.browser_state || 'unknown'}
            onClose={() => setSelectedWorker(null)}
          />
        )
      })()}
    </div>
  )
}
